import { Users, Clock, Warning } from "@phosphor-icons/react";

/**
 * Sticky trial + seat status banner shown at the top of the Org page.
 * Renders nothing if the tenant subscription has no trial / seat data.
 */
export default function SeatTrialBanner({ tenant }) {
  const sub = tenant?.subscription;
  if (!sub) return null;

  const isTrialing = sub.status === "trialing";
  const trialDaysLeft = sub.trial_days_left;
  const seatsPurchased = sub.seats_purchased;
  const seatsUsed = sub.seats_used ?? 0;
  const seatsAvailable = sub.seats_available;

  // Nothing to show — old tenant without seat data
  if (!isTrialing && seatsPurchased == null) return null;

  const trialAlmostOver = isTrialing && trialDaysLeft != null && trialDaysLeft <= 3;
  const seatsAlmostFull = seatsAvailable != null && seatsPurchased && seatsAvailable <= 1;

  return (
    <div className="mt-6 border border-zinc-950 grid grid-cols-1 sm:grid-cols-2" data-testid="seat-trial-banner">
      {/* Trial */}
      {isTrialing && (
        <div className={`p-5 border-b sm:border-b-0 sm:border-r border-zinc-200 ${trialAlmostOver ? "bg-[#FF3E00]/5" : "bg-amber-50"}`}>
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] text-zinc-600 uppercase">
            {trialAlmostOver ? <Warning size={12} weight="fill" className="text-[#FF3E00]" /> : <Clock size={12} />}
            {trialAlmostOver ? "TRIAL_ENDING_SOON" : "FREE_TRIAL_ACTIVE"}
          </div>
          <div className={`font-display font-black text-3xl mt-2 ${trialAlmostOver ? "text-[#FF3E00]" : "text-zinc-950"}`} data-testid="banner-trial-days">
            {trialDaysLeft ?? "?"} <span className="text-lg font-normal">day{trialDaysLeft === 1 ? "" : "s"} left</span>
          </div>
          <div className="text-[12px] text-zinc-600 mt-1">
            All Growth-plan modules included through {sub.trial_ends_at ? new Date(sub.trial_ends_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "trial end"}.
            {trialAlmostOver && " Add a payment method to keep going."}
          </div>
        </div>
      )}
      {/* Seats */}
      {seatsPurchased != null && (
        <div className={`p-5 ${seatsAlmostFull ? "bg-amber-50" : ""}`} data-testid="banner-seats">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] text-zinc-600 uppercase">
            <Users size={12} /> SEATS_IN_USE
          </div>
          <div className="font-display font-black text-3xl mt-2 tnum">
            <span data-testid="banner-seats-used">{seatsUsed}</span>
            <span className="text-zinc-400"> / {seatsPurchased}</span>
          </div>
          <div className="text-[12px] text-zinc-600 mt-1">
            {seatsAvailable > 0
              ? `${seatsAvailable} seat${seatsAvailable === 1 ? "" : "s"} available for new invitations.`
              : "You've reached your seat limit. Buy more seats to invite additional teammates."}
          </div>
          <a href="/billing/seats"
             data-testid="banner-buy-seats-cta"
             className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.12em] uppercase border border-zinc-950 px-3 py-1.5 hover:bg-zinc-950 hover:text-white transition-colors">
            BUY MORE SEATS →
          </a>
        </div>
      )}
    </div>
  );
}
